// Turbo Confirm Override
// This file overrides Turbo's default confirm behavior to use our custom ConfirmModal

import globalModalManager from './globalModalManager';

// Access Turbo from the global window object
declare global {
  interface Window {
    Turbo: any;
  }
}

const Turbo = window.Turbo;

// Check if the action seems destructive based on the message or element
function isDestructiveAction(message: string, element?: Element): boolean {
  const destructiveKeywords = ['delete', 'remove', 'destroy', 'clear', 'reset', 'sure?'];
  const lowerMessage = message.toLowerCase();

  // If the message clearly refers to creation/generation flows, treat it as non-destructive
  const nonDestructiveCreationKeywords = ['create', 'recreate', 'generate', 'update'];
  const isCreationFlow = nonDestructiveCreationKeywords.some(keyword => lowerMessage.includes(keyword));
  if (isCreationFlow) {
    return false;
  }

  // Check message content
  const hasDestructiveKeyword = destructiveKeywords.some(keyword => lowerMessage.includes(keyword));

  // Check element classes or data attributes for destructive actions
  if (element) {
    const elementClasses = element.className.toLowerCase();
    const hasDestructiveClass = elementClasses.includes('delete') ||
                               elementClasses.includes('remove') ||
                               elementClasses.includes('destroy') ||
                               elementClasses.includes('danger') ||
                               elementClasses.includes('red');

    const method = element.getAttribute('data-turbo-method');
    const isDeleteMethod = method === 'delete';

    return hasDestructiveKeyword || hasDestructiveClass || isDeleteMethod;
  }

  return hasDestructiveKeyword;
}

// Set up custom confirm method for Turbo
function setupTurboConfirmMethod() {
  if (!Turbo || !Turbo.setConfirmMethod) {
    console.warn('Turbo is not available. Turbo confirm override will not work.');
    return;
  }

  Turbo.setConfirmMethod((message: string, element: Element) => {
    return new Promise<boolean>((resolve) => {
      let resolved = false;

       // Listen for modal close to resolve with false if not already resolved
       const handleModalClose = () => {
        if (!resolved) {
          resolved = true;
          element.dispatchEvent(new CustomEvent('turbo-confirm:cancelled', { bubbles: true }));
          resolve(false);
        }
      };

      // Show our custom confirm modal
      globalModalManager.openModal('confirm', {
        message: message,
        onConfirm: () => {
          if (!resolved) {
            resolved = true;
            resolve(true);
          }
        },
        onClose: handleModalClose,
        variant: isDestructiveAction(message, element) ? 'danger' : 'default'
      });

      // Use a timeout to check if modal was closed without confirmation
      setTimeout(() => {
        const modalState = globalModalManager.getState();
        if (!modalState.isOpen && !resolved) {
          resolved = true;
          element.dispatchEvent(new CustomEvent('turbo-confirm:cancelled', { bubbles: true }));
          resolve(false);
        }
      }, 100);

      // Also listen for escape key or backdrop clicks
      const originalState = globalModalManager.getState();
      const checkModalClosed = setInterval(() => {
        const currentState = globalModalManager.getState();
        if (originalState.isOpen && !currentState.isOpen && !resolved) {
          resolved = true;
          element.dispatchEvent(new CustomEvent('turbo-confirm:cancelled', { bubbles: true }));
          resolve(false);
          clearInterval(checkModalClosed);
        }
      }, 100);

      // Clean up interval after 10 seconds maximum
      setTimeout(() => {
        clearInterval(checkModalClosed);
        if (!resolved) {
          resolved = true;
          // Dispatch cancelled event
          element.dispatchEvent(new CustomEvent('turbo-confirm:cancelled', { bubbles: true }));
          resolve(false);
        }
      }, 10000);
    });
  });
}

// Initialize the override when DOM is loaded
function initializeTurboConfirmOverride() {
  const init = () => {
    // Wait a bit for Turbo to be available
    if (window.Turbo) {
      setupTurboConfirmMethod();
    } else {
      // Retry after a short delay
      setTimeout(() => {
        if (window.Turbo) {
          setupTurboConfirmMethod();
        } else {
          console.warn('Turbo is still not available after waiting. Turbo confirm override will not work.');
        }
      }, 1000);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}

// Initialize immediately
initializeTurboConfirmOverride();

export default {
  setupTurboConfirmMethod,
  isDestructiveAction
};
